import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Loader2,
  Trash2,
  Download,
  X,
  Image as ImageIcon,
  ExternalLink,
  Upload,
} from 'lucide-react';
import { clsx } from 'clsx';
import { formatStorageBytes } from '@trove/note-sanitize';
import type { MediaUsageKind, MediaUsageRef, NoteMediaListItem } from '@trove/api-client';
import {
  deleteOrphanNoteMedia,
  isLikelyImageUploadFile,
  listNoteMedia,
  uploadNoteMediaFile,
  type ListNoteMediaParams,
} from '@/api/notesMedia';
import { NoteMediaQuotaError } from '@/utils/noteMediaQuota';
import { formatApiErrorMessage } from '@/utils/apiErrorMessage';
import { notifications } from '@/utils/notifications';
import { useAccountRole } from '@/hooks/useAccountRole';
import { SettingsReadOnlyBanner } from '@/components/settings/SettingsReadOnlyBanner';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { useSessionsStore } from '@/stores/sessions-store';

type DeleteConfirmTarget =
  | { kind: 'one'; mediaId: string }
  | { kind: 'bulk'; mediaIds: string[] }
  | null;

type StatusFilter = ListNoteMediaParams['status'];

const USAGE_KINDS: MediaUsageKind[] = [
  'note',
  'bookmark',
  'task',
  'team_chat',
  'assistant',
  'highlighter',
  'research_evidence',
];

function usageKindLabel(t: (key: string) => string, kind: MediaUsageKind): string {
  const map: Record<MediaUsageKind, string> = {
    note: t('storage.usage.note'),
    bookmark: t('storage.usage.bookmark'),
    task: t('storage.usage.task'),
    team_chat: t('storage.usage.teamChat'),
    assistant: t('storage.usage.assistant'),
    highlighter: t('storage.usage.highlighter'),
    research_evidence: t('storage.usage.researchEvidence'),
  };
  return map[kind] ?? kind;
}

function usageRefHref(ref: MediaUsageRef): string | null {
  if (ref.kind === 'note') return `/notes/edit/${encodeURIComponent(ref.resourceId)}`;
  if (ref.kind === 'task' && ref.projectSid) {
    return `/tasks/${encodeURIComponent(ref.projectSid)}`;
  }
  if (ref.kind === 'team_chat') return '/chat';
  if (ref.kind === 'bookmark') return `/troves?open=${encodeURIComponent(ref.resourceId)}`;
  if (ref.kind === 'research_evidence' && ref.projectSid) {
    return `/tabs/${encodeURIComponent(ref.projectSid)}/research/library?evidence=${encodeURIComponent(ref.resourceId)}`;
  }
  return null;
}

function UsageChip({ refItem, t }: { refItem: MediaUsageRef; t: (key: string, opts?: Record<string, string>) => string }) {
  const href = usageRefHref(refItem);
  const kind = usageKindLabel(t, refItem.kind);
  const title = refItem.isLocked
    ? t('storage.usage.locked')
    : refItem.title?.trim() || refItem.resourceId.slice(0, 8);
  const label = `${kind}: ${title}`;
  const className =
    'inline-flex max-w-full items-center gap-1 rounded-lg border border-border bg-muted/50 px-2 py-1 text-[11px] font-semibold text-foreground hover:border-primary hover:text-primary';

  if (!href) {
    return <span className={className}>{label}</span>;
  }
  return (
    <Link to={href} className={className}>
      <span className="truncate">{label}</span>
      <ExternalLink className="h-3 w-3 shrink-0 opacity-60" aria-hidden />
    </Link>
  );
}

export function StorageSettings() {
  const { t } = useTranslation('settings');
  const { canManageAccount } = useAccountRole();
  const readOnlyOrg = !canManageAccount;
  const { allSessions } = useSessionsStore();

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [items, setItems] = useState<NoteMediaListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [totalBytesUsed, setTotalBytesUsed] = useState(0);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [usageKindFilter, setUsageKindFilter] = useState<MediaUsageKind | ''>('');
  const [sidFilter, setSidFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<NoteMediaListItem | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmTarget>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const workspaceOptions = useMemo(
    () =>
      allSessions.map((s) => ({
        sid: s.sid,
        name: s.name || s.sid,
      })),
    [allSessions]
  );

  const load = useCallback(
    async (
      append = false,
      cursor?: string | null,
      overrides?: Partial<ListNoteMediaParams>
    ) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const data = await listNoteMedia({
          cursor: append ? cursor ?? undefined : undefined,
          limit: 48,
          sid: overrides?.sid ?? (sidFilter || undefined),
          status: overrides?.status ?? statusFilter,
          usageKind: overrides?.usageKind ?? (usageKindFilter || undefined),
        });
        setItems((prev) => (append ? [...prev, ...data.items] : data.items));
        setNextCursor(data.nextCursor);
        setTotalBytesUsed(data.totalBytesUsed);
        if (!append) setSelected(new Set());
      } catch {
        notifications.error(t('storage.loadFailed'));
        if (!append) setItems([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [sidFilter, statusFilter, usageKindFilter, t]
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  const orphanItems = useMemo(() => items.filter((i) => i.orphaned), [items]);
  const allOrphansSelected =
    orphanItems.length > 0 && orphanItems.every((i) => selected.has(i.mediaId));

  const toggleSelectOrphans = () => {
    if (allOrphansSelected) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(orphanItems.map((i) => i.mediaId)));
  };

  const onDeleteOne = async (mediaId: string) => {
    setBusyId(mediaId);
    try {
      await deleteOrphanNoteMedia(mediaId);
      notifications.success(t('storage.deleted'));
      setDeleteConfirm(null);
      await load(false);
    } catch {
      notifications.error(t('storage.deleteFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const onBulkDelete = async (mediaIds: string[]) => {
    if (mediaIds.length === 0) return;
    setBusyId('bulk');
    try {
      await Promise.all(mediaIds.map((id) => deleteOrphanNoteMedia(id)));
      notifications.success(t('storage.bulkDeleted', { count: mediaIds.length }));
      setDeleteConfirm(null);
      await load(false);
    } catch {
      notifications.error(t('storage.deleteFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.kind === 'one') {
      await onDeleteOne(deleteConfirm.mediaId);
    } else {
      await onBulkDelete(deleteConfirm.mediaIds);
    }
  };

  const handleUploadInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    // Reset after copying — clearing first empties the live FileList in most browsers.
    e.target.value = '';
    if (!picked.length || readOnlyOrg) return;
    const imageFiles = picked.filter(isLikelyImageUploadFile);
    if (imageFiles.length === 0) {
      notifications.error(t('storage.uploadInvalid'));
      return;
    }
    setUploading(true);
    let ok = 0;
    try {
      for (const file of imageFiles) {
        try {
          await uploadNoteMediaFile(file, { source: 'upload' });
          ok += 1;
        } catch (err) {
          const message =
            err instanceof NoteMediaQuotaError
              ? t('storage.uploadQuota')
              : formatApiErrorMessage(err, t, t('storage.uploadFailed'));
          notifications.error(message);
        }
      }
      if (ok > 0) {
        notifications.success(t('storage.uploadSuccess', { count: ok }));
        setStatusFilter('all');
        setUsageKindFilter('');
        setSidFilter('');
        await load(false, null, { status: 'all', sid: undefined, usageKind: undefined });
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-5xl animate-in space-y-6 pb-10 fade-in slide-in-from-bottom-2 duration-500">
      {readOnlyOrg ? <SettingsReadOnlyBanner /> : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <h2 className="text-2xl font-black tracking-tight text-foreground sm:text-3xl">{t('storage.title')}</h2>
          <p className="text-sm font-medium leading-relaxed text-muted-foreground">{t('storage.subtitle')}</p>
        </div>
        {!readOnlyOrg ? (
          <div className="flex shrink-0 flex-col items-stretch gap-2 sm:items-end">
            <button
              type="button"
              disabled={uploading}
              onClick={() => uploadInputRef.current?.click()}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-black text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Upload className="h-4 w-4" aria-hidden />
              )}
              {uploading ? t('storage.uploading') : t('storage.upload')}
            </button>
            <p className="text-[11px] text-muted-foreground sm:text-right">{t('storage.uploadHint')}</p>
          </div>
        ) : null}
      </div>
      <input
        ref={uploadInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => void handleUploadInput(e)}
      />

      <div className="flex flex-wrap items-center gap-2">
        {(['all', 'inUse', 'orphan'] as const).map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setStatusFilter(status)}
            className={clsx(
              'rounded-xl border px-3 py-1.5 text-xs font-black transition-colors',
              statusFilter === status
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:border-primary/40'
            )}
          >
            {t(`storage.filter.${status}`)}
          </button>
        ))}
        <select
          value={sidFilter}
          onChange={(e) => setSidFilter(e.target.value)}
          className="rounded-xl border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground"
          aria-label={t('storage.filter.workspace')}
        >
          <option value="">{t('storage.filter.allWorkspaces')}</option>
          {workspaceOptions.map((w) => (
            <option key={w.sid} value={w.sid}>
              {w.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled
          className="rounded-xl border border-primary bg-primary/10 px-3 py-1.5 text-xs font-black text-primary"
        >
          {t('storage.type.images')}
        </button>
        <button
          type="button"
          disabled
          title={t('storage.type.documentsSoon')}
          className="cursor-not-allowed rounded-xl border border-border px-3 py-1.5 text-xs font-black text-muted-foreground opacity-50"
        >
          {t('storage.type.documents')}
        </button>
        {USAGE_KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            disabled={kind === 'assistant'}
            onClick={() => setUsageKindFilter((prev) => (prev === kind ? '' : kind))}
            className={clsx(
              'rounded-xl border px-3 py-1.5 text-xs font-black transition-colors',
              kind === 'assistant' && 'cursor-not-allowed opacity-40',
              usageKindFilter === kind
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:border-primary/40'
            )}
          >
            {usageKindLabel(t, kind)}
          </button>
        ))}
      </div>

      {orphanItems.length > 0 && canManageAccount ? (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/5 px-4 py-3">
          <button type="button" onClick={toggleSelectOrphans} className="text-xs font-bold text-foreground underline">
            {allOrphansSelected ? t('storage.deselectAll') : t('storage.selectAllOrphans')}
          </button>
          {selected.size > 0 ? (
            <button
              type="button"
              disabled={busyId === 'bulk'}
              onClick={() => setDeleteConfirm({ kind: 'bulk', mediaIds: [...selected] })}
              className="inline-flex items-center gap-1.5 rounded-xl bg-red-500 px-3 py-1.5 text-xs font-black text-white disabled:opacity-50"
            >
              {busyId === 'bulk' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              {t('storage.freeUpSpace', { count: selected.size })}
            </button>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card px-5 py-8 text-center">
          <p className="text-sm text-muted-foreground">{t('storage.empty')}</p>
          {!readOnlyOrg ? (
            <button
              type="button"
              disabled={uploading}
              onClick={() => uploadInputRef.current?.click()}
              className="mt-4 inline-flex items-center gap-2 rounded-xl border border-primary px-4 py-2 text-sm font-black text-primary hover:bg-primary/10 disabled:opacity-50"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {t('storage.upload')}
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <p className="text-xs font-semibold text-muted-foreground">
            {t('storage.summary', {
              count: items.length,
              bytes: formatStorageBytes(totalBytesUsed),
            })}
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {items.map((item) => {
              const busy = busyId === item.mediaId;
              const isOrphan = item.orphaned;
              const alt =
                item.usageRefs.find((r) => r.kind === 'note' && r.title)?.title ?? t('storage.imageAlt');
              return (
                <div
                  key={item.mediaId}
                  className="group relative overflow-hidden rounded-2xl border border-border bg-card"
                >
                  {isOrphan && canManageAccount ? (
                    <label className="absolute left-2 top-2 z-10 flex h-6 w-6 cursor-pointer items-center justify-center rounded-md bg-background/90 shadow">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        checked={selected.has(item.mediaId)}
                        onChange={(e) => {
                          setSelected((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(item.mediaId);
                            else next.delete(item.mediaId);
                            return next;
                          });
                        }}
                      />
                    </label>
                  ) : null}
                  <button
                    type="button"
                    className="block aspect-square w-full overflow-hidden bg-muted"
                    onClick={() => setPreview(item)}
                  >
                    {item.publicUrl ? (
                      <img src={item.publicUrl} alt={alt} className="h-full w-full object-cover" loading="lazy" />
                    ) : (
                      <div className="flex h-full items-center justify-center text-muted-foreground">
                        <ImageIcon className="h-8 w-8" aria-hidden />
                      </div>
                    )}
                  </button>
                  <div className="space-y-2 p-2">
                    <p className="truncate text-[10px] font-bold text-muted-foreground">
                      {formatStorageBytes(item.byteLength)}
                      {isOrphan ? (
                        <span className="ml-1 text-amber-600">{t('storage.usage.orphan')}</span>
                      ) : null}
                    </p>
                    {item.usageRefs.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {item.usageRefs.slice(0, 2).map((ref) => (
                          <UsageChip
                            key={`${ref.kind}:${ref.resourceId}:${ref.linkType}`}
                            refItem={ref}
                            t={t}
                          />
                        ))}
                        {item.usageRefs.length > 2 ? (
                          <span className="text-[10px] text-muted-foreground">
                            +{item.usageRefs.length - 2}
                          </span>
                        ) : null}
                      </div>
                    ) : (
                      <span className="text-[10px] font-semibold text-amber-600">{t('storage.usage.orphan')}</span>
                    )}
                    <div className="flex gap-1">
                      {item.publicUrl ? (
                        <a
                          href={item.publicUrl}
                          download
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-lg border border-border p-1.5 text-muted-foreground hover:text-primary"
                          aria-label={t('storage.download')}
                        >
                          <Download className="h-3.5 w-3.5" />
                        </a>
                      ) : null}
                      {isOrphan && canManageAccount ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => setDeleteConfirm({ kind: 'one', mediaId: item.mediaId })}
                          className="rounded-lg border border-red-500/30 p-1.5 text-red-500 hover:bg-red-500/10 disabled:opacity-50"
                          aria-label={t('storage.delete')}
                        >
                          {busy ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {nextCursor ? (
            <div className="flex justify-center pt-4">
              <button
                type="button"
                disabled={loadingMore}
                onClick={() => void load(true, nextCursor)}
                className="rounded-xl border border-border px-4 py-2 text-sm font-black hover:border-primary disabled:opacity-50"
              >
                {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : t('storage.loadMore')}
              </button>
            </div>
          ) : null}
        </>
      )}

      {preview ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={t('storage.preview')}
        >
          <div className="relative max-h-[90vh] w-full max-w-3xl overflow-auto rounded-2xl bg-card p-4 shadow-xl">
            <button
              type="button"
              className="absolute right-3 top-3 rounded-lg border border-border p-2 hover:bg-muted"
              onClick={() => setPreview(null)}
              aria-label={t('storage.closePreview')}
            >
              <X className="h-4 w-4" />
            </button>
            {preview.publicUrl ? (
              <img
                src={preview.publicUrl}
                alt={preview.usageRefs[0]?.title ?? t('storage.imageAlt')}
                className="mx-auto max-h-[60vh] w-auto rounded-lg"
              />
            ) : null}
            <div className="mt-4 space-y-2">
              <p className="text-sm font-black text-foreground">{t('storage.usage.title')}</p>
              {preview.usageRefs.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {preview.usageRefs.map((ref) => (
                    <UsageChip key={`${ref.kind}:${ref.resourceId}:${ref.linkType}`} refItem={ref} t={t} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-amber-600">{t('storage.usage.orphan')}</p>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        isOpen={deleteConfirm !== null}
        onClose={() => {
          if (busyId) return;
          setDeleteConfirm(null);
        }}
        title={
          deleteConfirm?.kind === 'bulk'
            ? t('storage.bulkDeleteConfirmTitle')
            : t('storage.deleteConfirmTitle')
        }
        confirmLabel={t('storage.delete')}
        isConfirming={busyId !== null}
        onConfirm={() => void confirmDelete()}
      >
        <p className="text-sm text-muted-foreground">
          {deleteConfirm?.kind === 'bulk'
            ? t('storage.bulkDeleteConfirmBody', { count: deleteConfirm.mediaIds.length })
            : t('storage.deleteConfirmBody')}
        </p>
      </ConfirmModal>
    </div>
  );
}
